import React from 'react';
import SignatureCanvas from 'react-signature-canvas';
import Input from 'components/newComponents/Input';
import Icon from 'components/Icon';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import { preventArrowUpDown } from 'utils/preventArrowUpDown';
import showSuccess from 'utils/successHandler';
import showError from 'utils/errorHandler';
import Api from '../api';

const saveInitials = async (jobId, sign) => {
  const documentName = 'ld_contract_interstate_bill_client_initials';
  return await Api.sign(jobId, sign, documentName);
};
const saveSignature = async (jobId, sign) => {
  const documentName = 'ld_contract_interstate_bill_client';
  return await Api.sign(jobId, sign, documentName);
};
const saveCostValuation = async (jobId, value) => {
  return await Api.saveCostValuation(jobId, value);
};
const saveShipmentValuation = async (jobId, value) => {
  return await Api.saveShipmentValuation(jobId, value);
};

const CustomerSignForm = ({ jobId, insuranceOption, refetchContract, close, setPending }) => {
  const signature = React.useRef(null);
  const initials = React.useRef(null);

  const { copied, copy } = useCopyToClipboard({ resetDelayMs: 3000 });

  const [costValuation, setCostValuation] = React.useState('');
  const [costValuationError, setCostValuationError] = React.useState('');
  const [shipmentValuation, setShipmentValuation] = React.useState('');
  const [shipmentValuationError, setShipmentValuationError] = React.useState('');

  const submitWithShipmentValuation = async () => {
    if (signature.current.isEmpty()) return;
    if (shipmentValuation.trim().length === 0) {
      setShipmentValuationError('Required');
      return;
    }

    setPending(true);

    try {
      await saveSignature(jobId, signature.current.toDataURL('base64'));
      await saveShipmentValuation(jobId, shipmentValuation.trim());
      await refetchContract();
      showSuccess({ message: 'Success' });
      close();
    } catch (e) {}

    setPending(false);
  };

  const submitWithCostValuation = async () => {
    if (signature.current.isEmpty() || initials.current.isEmpty()) return;

    if (costValuation.trim().length === 0) {
      setCostValuationError('Required');
      return;
    }

    const costValuationRegex = /\b\d+\s+CENTS PER POUND PER ARTICLE\b/i;

    if (!costValuationRegex.test(costValuation.trim())) {
      setCostValuationError('Wrong pattern');
      showError({ message: 'Must match the pattern: “60 CENTS PER POUND PER ARTICLE”' });
      return;
    }

    setPending(true);

    try {
      await saveSignature(jobId, signature.current.toDataURL('base64'));
      await saveInitials(jobId, initials.current.toDataURL('base64'));
      await saveCostValuation(jobId, costValuation.trim());
      await refetchContract();
      showSuccess({ message: 'Success' });

      setTimeout(() => {
        close();
        window.location.reload();
      }, 500);
    } catch (e) {}

    setPending(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (insuranceOption === 1) submitWithShipmentValuation();
    if (insuranceOption === 2) submitWithCostValuation();
  };

  return (
    <form id="ld-contract-invoice-modal" onSubmit={handleSubmit}>
      <div className="ld-contract-invoice-modal-body">
        {insuranceOption === 1 && (
          <React.Fragment>
            <div className="new-table">
              <div className="new-table--row">
                <div className="new-table--row--item select">
                  <Input
                    onKeyDown={preventArrowUpDown}
                    type="number"
                    value={shipmentValuation}
                    onChange={(e) => {
                      setShipmentValuation(e.target.value);
                      setShipmentValuationError('');
                    }}
                    placeholder="The Total Value of my shipment is:"
                  />

                  {shipmentValuationError && <p className="error flex-shrink-0">{shipmentValuationError}</p>}
                </div>
              </div>
            </div>

            <div className="new-table">
              <div className="new-table--row">
                <div className="new-table--row--item select light">
                  <label className="ld-contract-invoice-modal-label">Customer Signature</label>
                </div>
              </div>
              <div className="new-table--row">
                <div className="new-table--row--item no-padding">
                  <SignatureCanvas ref={signature} canvasProps={{ className: 'ld-contract-invoice-modal-sign-field' }} />
                </div>
              </div>
            </div>
          </React.Fragment>
        )}

        {insuranceOption === 2 && (
          <React.Fragment>
            <div className="new-table">
              <div className="new-table--row">
                <div className="new-table--row--item select light">
                  <label className="ld-contract-invoice-modal-label">IF SELECTED, PLEASE WRITE: “60 CENTS PER POUND PER ARTICLE”</label>
                  <button type="button" className="flex-shrink-0" onClick={() => copy('60 CENTS PER POUND PER ARTICLE')}>
                    {copied ? (
                      <span className="d-flex align-items-center">
                        <Icon size={12} icon="booked" />
                        Copied
                      </span>
                    ) : (
                      <span>Copy text</span>
                    )}
                  </button>
                </div>
              </div>
              <div className="new-table--row">
                <div className="new-table--row--item select">
                  <Input
                    type="text"
                    placeholder="Please enter text"
                    value={costValuation}
                    onChange={(e) => {
                      setCostValuation(e.target.value.toUpperCase());
                      setCostValuationError('');
                    }}
                  />
                  {costValuationError && <p className="error flex-shrink-0">{costValuationError}</p>}
                </div>
              </div>
            </div>

            <div className="new-table">
              <div className="new-table--row">
                <div className="new-table--row--item select light">
                  <label className="ld-contract-invoice-modal-label">Customer Initials</label>
                </div>
              </div>
              <div className="new-table--row">
                <div className="new-table--row--item no-padding">
                  <SignatureCanvas ref={initials} canvasProps={{ className: 'ld-contract-invoice-modal-sign-field' }} />
                </div>
              </div>
              <div className="new-table--row">
                <div className="new-table--row--item select light">
                  <label className="ld-contract-invoice-modal-label">Customer Signature</label>
                </div>
              </div>
              <div className="new-table--row">
                <div className="new-table--row--item no-padding">
                  <SignatureCanvas ref={signature} canvasProps={{ className: 'ld-contract-invoice-modal-sign-field' }} />
                </div>
              </div>
            </div>
          </React.Fragment>
        )}
      </div>
    </form>
  );
};

export default CustomerSignForm;
